import PocketBase, { type RecordModel } from 'pocketbase';
import type { User } from '../../../shared/types';

/**
 * The whole data layer.
 *
 * This file replaces v1's `googleSheets.ts` (1,712 lines) and `firebase.ts`.
 * Most of what it replaced was fuzzy header matching, positional column
 * fallbacks and a mock-token system — all of which existed only because the
 * database was a spreadsheet.
 */

// Same origin: Caddy routes /api to PocketBase, so there is no cross-origin
// setup and no API base URL to configure per environment.
export const pb = new PocketBase(window.location.origin);

pb.autoCancellation(false);

export type AuthUser = User & RecordModel;

export function currentUser(): AuthUser | null {
  // An expired token must read as signed-out, or the workspace renders and
  // every request 401s with no way back to the sign-in screen.
  if (!pb.authStore.isValid) return null;
  return (pb.authStore.record as AuthUser | null) ?? null;
}

/**
 * Validates the stored token against the server on app start. A token that is
 * still within its lifetime but revoked server-side would otherwise leave the
 * app "signed in" with every request failing.
 */
export async function refreshAuth(): Promise<void> {
  if (!pb.authStore.isValid) {
    pb.authStore.clear();
    return;
  }
  try {
    await pb.collection('users').authRefresh();
  } catch (err) {
    if ((err as { status?: number }).status === 401) pb.authStore.clear();
    // Other failures (offline, server restarting) keep the stored auth.
  }
}

export function isAdmin(): boolean {
  return currentUser()?.role === 'admin';
}

/**
 * Google sign-in.
 *
 * The `@theimpactbrands.com` restriction is enforced by a PocketBase hook, not
 * here — v1 checked the domain in the browser and signed the user out if it
 * failed, which anyone could bypass by not running that code.
 */
export async function signIn(): Promise<AuthUser> {
  const result = await pb.collection('users').authWithOAuth2({ provider: 'google' });
  return result.record as AuthUser;
}

export function signOut(): void {
  pb.authStore.clear();
}

export function onAuthChange(fn: (user: AuthUser | null) => void): () => void {
  return pb.authStore.onChange(() => fn(currentUser()));
}

/** Turns a PocketBase error into something worth showing a person. */
export function errorText(err: unknown): string {
  if (!err) return 'Something went wrong.';

  const e = err as {
    message?: string;
    response?: { message?: string; data?: Record<string, { message?: string }> };
  };

  const fieldErrors = e.response?.data;
  if (fieldErrors && Object.keys(fieldErrors).length) {
    const parts = Object.entries(fieldErrors)
      .map(([field, detail]) => `${field}: ${detail?.message ?? 'invalid'}`)
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }

  return e.response?.message || e.message || 'Something went wrong.';
}

/** PocketBase stores dates as `2006-01-02 15:04:05.000Z`. */
export function parseDate(value: string | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatWhen(value: string | undefined): string {
  const d = parseDate(value);
  if (!d) return '—';

  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;

  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return `${minutes}m ${rest}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
